import React, { useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthContext, AuthProvider } from './context/AuthContext';
import AuthPage from './pages/AuthPage';
import PublicPortal from './components/PublicPortal/PublicPortal';
import PublicCourseDetail from './components/PublicPortal/PublicCourseDetail';
import PublicJobDetail from './components/PublicPortal/PublicJobDetail';
import BrowseCourses from './components/PublicPortal/BrowseCourses';
import BrowseJobs from './components/PublicPortal/BrowseJobs';
import Loader from './components/shared/Loader';
// import SuperAdminDashboard from './pages/SuperAdminDashboard'; // Replacing with layout
import SuperAdminLayout from './components/SuperAdmin/SuperAdminLayout';
import DashboardOverview from './components/SuperAdmin/views/DashboardOverview';
import Settings from './components/SuperAdmin/views/Settings';
import ProfileSection from './components/SuperAdmin/views/ProfileSection';
import UserManagement from './components/SuperAdmin/views/UserManagement';
import CourseManagement from './components/SuperAdmin/views/CourseManagement';
import JobModeration from './components/SuperAdmin/views/JobModeration';
import Payments from './components/SuperAdmin/views/Payments';
import SupportAndNotifications from './components/SuperAdmin/views/SupportAndNotifications';
import AuditLog from './components/SuperAdmin/views/AuditLog';
import SuperAdminNotifications from './components/SuperAdmin/views/SuperAdminNotifications';

// Tutor Views 
import TutorDashboardOverview from './components/Tutor/views/TutorDashboardOverview';
import TutorCourses from './components/Tutor/views/TutorCourses';
import TutorLMS from './components/Tutor/views/TutorLMS';
import TutorLiveClasses from './components/Tutor/views/TutorLiveClasses';
import TutorAssignments from './components/Tutor/views/TutorAssignments';
import TutorStudents from './components/Tutor/views/TutorStudents';
import TutorAttendance from './components/Tutor/views/TutorAttendance';
import TutorProfile from './components/Tutor/views/TutorProfile';
import TutorNotifications from './components/Tutor/views/TutorNotifications';
import TutorSupport from './components/Tutor/views/TutorSupport';

// Student Views
import StudentLayout from './components/Student/StudentLayout';
import Overview from './components/Student/views/Overview';
import MyCourses from './components/Student/views/MyCourses';
import CourseCatalog from './components/Student/views/CourseCatalog';
import StudentJobPortal from './components/Student/views/StudentJobPortal';
import StudentApplications from './components/Student/views/StudentApplications';
import StudentProfile from './components/Student/views/StudentProfile';
import StudentSupport from './components/Student/views/StudentSupport';
import StudentNotifications from './components/Student/views/StudentNotifications';
import StudentInvoices from './components/Student/views/StudentInvoices';
import CourseDetail from './components/Student/views/CourseDetail';
import LearningPortal from './components/Student/views/LearningPortal';
import StudentAssessments from './components/Student/views/StudentAssessments';

// Recruiter Views
import RecruiterLayout from './components/Recruiter/RecruiterLayout';
import HiringHub from './components/Recruiter/views/HiringHub';
import CompanyBranding from './components/Recruiter/views/CompanyBranding';
import JobLifecycle from './components/Recruiter/views/JobLifecycle';
import TalentSourcing from './components/Recruiter/views/TalentSourcing';
import InterviewCenter from './components/Recruiter/views/InterviewCenter';
import RecruiterMessaging from './components/Recruiter/views/RecruiterMessaging';
import RecruiterReports from './components/Recruiter/views/RecruiterReports';
import RecruiterProfile from './components/Recruiter/views/RecruiterProfile';
import RecruiterNotifications from './components/Recruiter/views/RecruiterNotifications';

// Job Seeker Views
import JobSeekerLayout from './components/JobSeeker/JobSeekerLayout';
import JobSeekerOverview from './components/JobSeeker/views/JobSeekerOverview';
import JobBoard from './components/JobSeeker/views/JobBoard';
import ApplicationsTracker from './components/JobSeeker/views/ApplicationsTracker';
import JobSeekerProfile from './components/JobSeeker/views/JobSeekerProfile';
import JobSeekerSupport from './components/JobSeeker/views/JobSeekerSupport';
import JobSeekerNotifications from './components/JobSeeker/views/JobSeekerNotifications';

import { API_URL } from './config';
import axios from 'axios';
import Default from './components/Default';

// Configure Axios Defaults
axios.defaults.baseURL = API_URL; // Centrally controlled API URL

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useContext(AuthContext);

  if (loading) return <Loader fullScreen={true} />;

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/auth" replace />;
  }

  return children;
};

// No more placeholders! Components imported successfully.


function AppWrapper() {
  const { user } = useContext(AuthContext);

  return (
    <Routes>
      {/* <Route path="/" element={<AuthPage />} /> */}
      <Route path="/" element={<PublicPortal />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/browse/courses" element={<BrowseCourses />} />
      <Route path="/browse/jobs" element={<BrowseJobs />} />

      {/* Standalone Course Detail Page — opens in new tab */}
      <Route path="/course-detail/:courseId" element={<CourseDetail />} />
      <Route path="/public-course/:courseId" element={<PublicCourseDetail />} />
      <Route path="/public-job/:jobId" element={<PublicJobDetail />} />

      {/* Pathway A: Candidate/Student route */}
      <Route
        path="/student-dashboard"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <StudentLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Overview />} />
        <Route path="courses" element={<MyCourses />} />
        <Route path="assessments" element={<StudentAssessments />} />
        <Route path="catalog" element={<CourseCatalog />} />
        <Route path="jobs" element={<StudentJobPortal />} />
        <Route path="applications" element={<StudentApplications />} />
        <Route path="profile" element={<StudentProfile />} />
        <Route path="support" element={<StudentSupport />} />
        <Route path="notifications" element={<StudentNotifications />} />
        <Route path="invoices" element={<StudentInvoices />} />
        <Route path="learning/:courseId" element={<LearningPortal />} />
      </Route>

      {/* Pathway Pure Job Seeker: Professional Dash */}
      <Route
        path="/jobseeker-dashboard"
        element={
          <ProtectedRoute allowedRoles={['job_seeker', 'student']}>
            <JobSeekerLayout />
            {/* <Default /> */}
          </ProtectedRoute>
        }
      >
        <Route index element={<JobSeekerOverview />} />
        <Route path="jobs" element={<JobBoard />} />
        <Route path="applications" element={<ApplicationsTracker />} />
        <Route path="profile" element={<JobSeekerProfile />} />
        <Route path="notifications" element={<JobSeekerNotifications />} />
        <Route path="support" element={<JobSeekerSupport />} />
      </Route>

      {/* Pathway B / Admin: Internal Staff routes (Unified under Super Admin pattern) */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'recruiter', 'course_admin', 'finance_admin', 'support_staff', 'reports_staff', 'tutor']}>
            <SuperAdminLayout />
          </ProtectedRoute>
        }
      >
        {/* Nested Admin Routes */}
        <Route index element={
          user?.role === 'tutor' ? <TutorDashboardOverview /> : <DashboardOverview />
        } />
        <Route path="users" element={<UserManagement />} />
        <Route path="courses" element={<CourseManagement />} />
        <Route path="jobs" element={<JobModeration />} />
        <Route path="payments" element={<Payments />} />
        <Route path="support" element={<SupportAndNotifications />} />
        <Route path="audit" element={<AuditLog />} />
        <Route path="notifications" element={<SuperAdminNotifications />} />
        <Route path="settings" element={<Settings />} />
        <Route path="profile" element={
          user?.role === 'tutor' ? <TutorProfile /> : <ProfileSection />
        } />

        {/* Nested Tutor Routes (Also under /admin namespace for unified layout authentication) */}
        <Route path="tutor-courses" element={<TutorCourses />} />
        <Route path="tutor-lms" element={<TutorLMS />} />
        <Route path="tutor-live-classes" element={<TutorLiveClasses />} />
        <Route path="tutor-assignments" element={<TutorAssignments />} />
        <Route path="tutor-students" element={<TutorStudents />} />
        <Route path="tutor-attendance" element={<TutorAttendance />} />
        <Route path="tutor-notifications" element={<TutorNotifications />} />
        <Route path="tutor-support" element={<TutorSupport />} />
      </Route>

      {/* Pathway C: Recruiter Dashboard */}
      <Route
        path="/recruiter"
        element={
          <ProtectedRoute allowedRoles={['recruiter']}>
            <RecruiterLayout />
            {/* <Default /> */}
          </ProtectedRoute>
        }
      >
        <Route index element={<HiringHub />} />
        <Route path="company" element={<CompanyBranding />} />
        <Route path="jobs" element={<JobLifecycle />} />
        <Route path="applicants" element={<TalentSourcing />} />
        <Route path="interviews" element={<InterviewCenter />} />
        <Route path="messages" element={<RecruiterMessaging />} />
        <Route path="reports" element={<RecruiterReports />} />
        <Route path="profile" element={<RecruiterProfile />} />
        <Route path="notifications" element={<RecruiterNotifications />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppWrapper />
      </Router>
    </AuthProvider>
  );
}

export default App;
